/**
 * The single source of status presentation. Every state in the product maps to a
 * group (colour treatment) and a glyph (shape), so status never relies on colour alone.
 * Feature code passes a state value; it never chooses colours.
 *
 * Presentation (StatusBadge): neutral = grey glyph + ink · progress = blue glyph + text · positive = green check +
 * text · attention = amber glyph + text on the row's own surface with a 3px amber inline-start rule (never a cream
 * fill) · negative = red glyph + text · final = the one solid badge, reserved for DISBURSED · final-muted = grey.
 */
import type { Schema } from '@dealdesk/api-schema';

export type StatusGroup = 'neutral' | 'progress' | 'attention' | 'positive' | 'negative' | 'final' | 'final-muted';
export type Glyph =
  | 'dashed'
  | 'empty'
  | 'quarter'
  | 'half'
  | 'three-quarter'
  | 'bank'
  | 'attention'
  | 'return'
  | 'query'
  | 'check'
  | 'cross'
  | 'check-filled'
  | 'slash'
  | 'clock'
  | 'lock'
  | 'pause';

export interface StatusDef {
  group: StatusGroup;
  glyph: Glyph;
  /** Translation key in the "status" namespace (business/status/i18n/<code>.json). Never a literal label. */
  labelKey: string;
}

type Registry<K extends string> = Record<K, StatusDef>;
const def = (group: StatusGroup, glyph: Glyph, labelKey: string): StatusDef => ({ group, glyph, labelKey });

export const dealStage: Registry<Schema<'DealStage'>> = {
  DRAFT: def('neutral', 'dashed', 'dealStage.DRAFT'),
  DOCUMENTS_PENDING: def('attention', 'attention', 'dealStage.DOCUMENTS_PENDING'),
  OPS_REVIEW: def('progress', 'quarter', 'dealStage.OPS_REVIEW'),
  OPS_RETURNED: def('attention', 'return', 'dealStage.OPS_RETURNED'),
  SUBMITTED_TO_BANK: def('progress', 'half', 'dealStage.SUBMITTED_TO_BANK'),
  BANK_REVIEW: def('progress', 'bank', 'dealStage.BANK_REVIEW'),
  BANK_QUERY: def('attention', 'query', 'dealStage.BANK_QUERY'),
  APPROVED: def('positive', 'check', 'dealStage.APPROVED'),
  REJECTED: def('negative', 'cross', 'dealStage.REJECTED'),
  POST_APPROVAL: def('progress', 'three-quarter', 'dealStage.POST_APPROVAL'),
  BOOKED: def('positive', 'check', 'dealStage.BOOKED'),
  DISBURSED: def('final', 'check-filled', 'dealStage.DISBURSED'),
  CANCELLED: def('final-muted', 'slash', 'dealStage.CANCELLED'),
};

export const documentStatus: Registry<Schema<'DocumentStatus'>> = {
  missing: def('attention', 'empty', 'document.missing'),
  scanning: def('progress', 'clock', 'document.scanning'),
  uploaded: def('progress', 'half', 'document.uploaded'),
  under_review: def('progress', 'three-quarter', 'document.under_review'),
  approved: def('positive', 'check', 'document.approved'),
  rejected: def('negative', 'cross', 'document.rejected'),
  expired: def('negative', 'clock', 'document.expired'),
};

/** Malware scan of a stored file (attachments, document versions, settlement files). Infected = quarantined. */
export const fileScanStatus: Registry<Schema<'FileScanStatus'>> = {
  pending: def('progress', 'clock', 'fileScan.pending'),
  clean: def('positive', 'check', 'fileScan.clean'),
  infected: def('negative', 'cross', 'fileScan.infected'),
  failed: def('negative', 'cross', 'fileScan.failed'),
};

export const stepStatus: Registry<Schema<'StepStatus'>> = {
  not_started: def('neutral', 'empty', 'step.not_started'),
  in_progress: def('progress', 'half', 'step.in_progress'),
  done: def('positive', 'check', 'step.done'),
  skipped: def('final-muted', 'slash', 'step.skipped'),
};

export const receivableStatus: Registry<Schema<'ReceivableStatus'>> = {
  pending: def('neutral', 'empty', 'receivable.pending'),
  invoiced: def('progress', 'half', 'receivable.invoiced'),
  partially_collected: def('progress', 'three-quarter', 'receivable.partially_collected'),
  collected: def('positive', 'check', 'receivable.collected'),
  disputed: def('attention', 'attention', 'receivable.disputed'),
  written_off: def('final-muted', 'slash', 'receivable.written_off'),
};

export const invoiceStatus: Registry<Schema<'InvoiceStatus'>> = {
  issued: def('progress', 'quarter', 'invoice.issued'),
  partially_paid: def('progress', 'three-quarter', 'invoice.partially_paid'),
  paid: def('positive', 'check', 'invoice.paid'),
  overdue: def('negative', 'clock', 'invoice.overdue'),
  cancelled: def('final-muted', 'slash', 'invoice.cancelled'),
};

export const paymentStatus: Registry<Schema<'PaymentStatus'>> = {
  unallocated: def('attention', 'empty', 'payment.unallocated'),
  partially_allocated: def('attention', 'half', 'payment.partially_allocated'),
  allocated: def('positive', 'check', 'payment.allocated'),
  voided: def('final-muted', 'slash', 'payment.voided'),
};

export const reconResult: Registry<Schema<'ReconResult'>> = {
  matched: def('positive', 'check', 'recon.matched'),
  difference: def('attention', 'attention', 'recon.difference'),
  missing_at_bank: def('negative', 'cross', 'recon.missing_at_bank'),
  unmatched: def('attention', 'query', 'recon.unmatched'),
};

export const reconRunStatus: Registry<'review' | 'posted'> = {
  review: def('progress', 'half', 'reconRun.review'),
  posted: def('positive', 'check', 'reconRun.posted'),
};

/** Staff commission (payable): payout runs, entry payout state and rule effective state. */
export const staffPayoutRunStatus: Registry<Schema<'StaffPayoutRunStatus'>> = {
  draft: def('neutral', 'dashed', 'staffPayoutRun.draft'),
  approved: def('progress', 'three-quarter', 'staffPayoutRun.approved'),
  paid: def('positive', 'check', 'staffPayoutRun.paid'),
};

export const staffPayoutStatus: Registry<Schema<'StaffPayoutStatus'>> = {
  unpaid: def('neutral', 'empty', 'staffPayout.unpaid'),
  in_run: def('progress', 'half', 'staffPayout.in_run'),
  paid: def('positive', 'check', 'staffPayout.paid'),
};

export const staffRuleState: Registry<Schema<'StaffRuleState'>> = {
  active: def('positive', 'check', 'staffRule.active'),
  scheduled: def('progress', 'clock', 'staffRule.scheduled'),
  expired: def('final-muted', 'slash', 'staffRule.expired'),
};

export const versionState: Registry<Schema<'VersionState'>> = {
  draft: def('attention', 'dashed', 'version.draft'),
  published: def('positive', 'check', 'version.published'),
  retired: def('final-muted', 'lock', 'version.retired'),
};

export const userStatus: Registry<Schema<'UserStatus'>> = {
  active: def('positive', 'check', 'user.active'),
  invited: def('progress', 'clock', 'user.invited'),
  suspended: def('negative', 'slash', 'user.suspended'),
  deactivated: def('final-muted', 'slash', 'user.deactivated'),
};

// ---------------------------------------------------------------- Architecture v2

export const liabilityLetterState: Registry<Schema<'LiabilityLetterState'>> = {
  not_required: def('final-muted', 'slash', 'liabilityLetter.not_required'),
  required: def('neutral', 'empty', 'liabilityLetter.required'),
  requested: def('progress', 'half', 'liabilityLetter.requested'),
  available: def('positive', 'check', 'liabilityLetter.available'),
  expiring: def('attention', 'clock', 'liabilityLetter.expiring'),
  expired: def('negative', 'cross', 'liabilityLetter.expired'),
  reissue_requested: def('attention', 'return', 'liabilityLetter.reissue_requested'),
  used: def('final-muted', 'lock', 'liabilityLetter.used'),
};

export const shortfallStatus: Registry<Schema<'ShortfallStatus'>> = {
  pending: def('attention', 'attention', 'shortfall.pending'),
  funded: def('positive', 'check', 'shortfall.funded'),
  waived: def('final-muted', 'slash', 'shortfall.waived'),
};

export const settlementStatus: Registry<Schema<'SettlementItem'>['settlementStatus']> = {
  pending: def('neutral', 'empty', 'settlement.pending'),
  mc_issued: def('progress', 'half', 'settlement.mc_issued'),
  settled: def('positive', 'check', 'settlement.settled'),
};

export const clearanceStatus: Registry<Schema<'SettlementItem'>['clearanceStatus']> = {
  pending: def('neutral', 'empty', 'clearance.pending'),
  received: def('positive', 'check', 'clearance.received'),
  not_required: def('final-muted', 'slash', 'clearance.not_required'),
};

export const enlistmentStatus: Registry<Schema<'EnlistmentStatus'>> = {
  requested: def('progress', 'quarter', 'enlistment.requested'),
  documents_pending: def('attention', 'attention', 'enlistment.documents_pending'),
  submitted_to_bank: def('progress', 'bank', 'enlistment.submitted_to_bank'),
  enlisted: def('positive', 'check', 'enlistment.enlisted'),
  declined: def('negative', 'cross', 'enlistment.declined'),
};

export const bankUpdateOutcome: Registry<Schema<'BankUpdateOutcome'>> = {
  open: def('attention', 'query', 'bankUpdateOutcome.open'),
  resolved: def('positive', 'check', 'bankUpdateOutcome.resolved'),
  waived: def('final-muted', 'slash', 'bankUpdateOutcome.waived'),
  approved: def('positive', 'check', 'bankUpdateOutcome.approved'),
  declined: def('negative', 'cross', 'bankUpdateOutcome.declined'),
  noted: def('neutral', 'empty', 'bankUpdateOutcome.noted'),
};

export const accountStatus: Registry<Schema<'AccountStatus'>> = {
  active: def('positive', 'check', 'account.active'),
  dormant: def('attention', 'clock', 'account.dormant'),
  blacklisted: def('negative', 'cross', 'account.blacklisted'),
  none: def('neutral', 'empty', 'account.none'),
};

export const employerStatus: Registry<Schema<'EmployerStatus'>> = {
  active: def('positive', 'check', 'employer.active'),
  inactive: def('final-muted', 'slash', 'employer.inactive'),
  merged: def('final-muted', 'return', 'employer.merged'),
};

/** C3: an institution of the bank master; inactive = not offered for new selections (e.g. not on the CBUAE register). */
export const institutionStatus: Registry<'active' | 'inactive'> = {
  active: def('positive', 'check', 'institution.active'),
  inactive: def('final-muted', 'slash', 'institution.inactive'),
};

/**
 * How a bank treats a customer's employer (terminology used everywhere: employers list, customer employments, deal
 * form and application). Labels take {{bank}}. Not on the list = attention; no classification = neutral with the
 * dashed (outline) glyph; employer not in the master = attention (Operations links it on Customer employers).
 */
export const employerListing: Registry<'listed' | 'not_listed' | 'unclassified' | 'not_in_master'> = {
  listed: def('neutral', 'check', 'employerListing.listed'),
  not_listed: def('attention', 'attention', 'employerListing.not_listed'),
  unclassified: def('neutral', 'dashed', 'employerListing.unclassified'),
  not_in_master: def('attention', 'query', 'employerListing.not_in_master'),
};

export const documentVerification: Registry<Schema<'DocumentVerification'>> = {
  none: def('neutral', 'empty', 'verification.none'),
  copy: def('progress', 'half', 'verification.copy'),
  original_seen: def('positive', 'check', 'verification.original_seen'),
};

/** Hold is orthogonal to the stage; the badge shows it next to the stage. */
export const holdState: Registry<'on_hold' | 'resumes_today' | 'overdue'> = {
  on_hold: def('attention', 'pause', 'hold.on_hold'),
  resumes_today: def('attention', 'pause', 'hold.resumes_today'),
  overdue: def('negative', 'pause', 'hold.overdue'),
};

export const importStatus: Registry<Schema<'ImportStatus'>> = {
  preview: def('progress', 'half', 'import.preview'),
  committed: def('positive', 'check', 'import.committed'),
  failed: def('negative', 'cross', 'import.failed'),
  discarded: def('final-muted', 'slash', 'import.discarded'),
};

export const importRowAction: Registry<Schema<'ImportRowAction'>> = {
  create: def('progress', 'quarter', 'importRow.create'),
  update: def('progress', 'half', 'importRow.update'),
  skip: def('final-muted', 'slash', 'importRow.skip'),
  conflict: def('attention', 'attention', 'importRow.conflict'),
  error: def('negative', 'cross', 'importRow.error'),
};

/** A5: the deal's state of a bank form (official bank template): its signed copy, or nothing when no signature is needed. */
export const bankFormStatus: Registry<Schema<'DealBankFormStatus'>> = {
  not_required: def('neutral', 'dashed', 'bankForm.not_required'),
  not_started: def('neutral', 'empty', 'bankForm.not_started'),
  signed_copy_uploaded: def('progress', 'half', 'bankForm.signed_copy_uploaded'),
  reviewed: def('positive', 'check', 'bankForm.reviewed'),
  rejected: def('negative', 'cross', 'bankForm.rejected'),
};

export const registries = {
  bankFormStatus,
  liabilityLetterState,
  shortfallStatus,
  settlementStatus,
  clearanceStatus,
  enlistmentStatus,
  bankUpdateOutcome,
  accountStatus,
  employerStatus,
  institutionStatus,
  employerListing,
  documentVerification,
  holdState,
  importStatus,
  importRowAction,
  dealStage,
  documentStatus,
  fileScanStatus,
  stepStatus,
  receivableStatus,
  invoiceStatus,
  paymentStatus,
  reconResult,
  reconRunStatus,
  staffPayoutRunStatus,
  staffPayoutStatus,
  staffRuleState,
  versionState,
  userStatus,
} as const;

export type RegistryName = keyof typeof registries;
export type RegistryValue<R extends RegistryName> = keyof (typeof registries)[R] & string;
